import { format } from 'date-fns'

import {
  IntervalType,
  MetricsCountDto,
  MetricsDepositsDto,
  MetricsIBCommissionsDto,
  MetricsLotsDto,
  MetricsNVDto,
  MetricsNetDepositsDto,
} from '@model/IntroducingBrokerMetricsDto'

import {
  ACTIVE_CLIENTS_METRICS_CATEGORY,
  ACTIVE_TA_METRICS_CATEGORY,
  DEPOSITS_METRICS_CATEGORY,
  FTD_METRICS_CATEGORY,
  IB_COMMISSIONS_METRICS_CATEGORY,
  LOTS_METRICS_CATEGORY,
  NET_DEPOSITS_METRICS_CATEGORY,
  NEW_CLIENTS_METRICS_CATEGORY,
  NV_METRICS_CATEGORY,
  OPENED_TA_METRICS_CATEGORY,
} from '../consts'

type TMetrics =
  | MetricsCountDto
  | MetricsDepositsDto
  | MetricsIBCommissionsDto
  | MetricsLotsDto
  | MetricsNetDepositsDto
  | MetricsNVDto

type TData =
  | MetricsCountDto[]
  | MetricsDepositsDto[]
  | MetricsIBCommissionsDto[]
  | MetricsLotsDto[]
  | MetricsNetDepositsDto[]
  | MetricsNVDto[]

export type TMetricsCategory =
  | typeof NV_METRICS_CATEGORY
  | typeof LOTS_METRICS_CATEGORY
  | typeof FTD_METRICS_CATEGORY
  | typeof DEPOSITS_METRICS_CATEGORY
  | typeof NET_DEPOSITS_METRICS_CATEGORY
  | typeof NEW_CLIENTS_METRICS_CATEGORY
  | typeof ACTIVE_CLIENTS_METRICS_CATEGORY
  | typeof OPENED_TA_METRICS_CATEGORY
  | typeof ACTIVE_TA_METRICS_CATEGORY
  | typeof IB_COMMISSIONS_METRICS_CATEGORY

export const processChartData = (
  data: TData | undefined,
  category: TMetricsCategory,
  locale: Locale
) => {
  if (!data) {
    return []
  }

  return data.map((metrics, index) => {
    const date = index === 0 ? metrics.startDate : metrics.endDate

    const shortFormat = metrics.intervalType.id === IntervalType.Daily ? 'd MMM' : 'MMM yy'
    const longFormat = metrics.intervalType.id === IntervalType.Daily ? 'd MMMM' : 'MMMM yyyy'

    const name = format(new Date(date), shortFormat, { locale })
    const tooltipLabel = format(new Date(date), longFormat, { locale })

    return {
      name: name.toUpperCase(),
      tooltipLabel: tooltipLabel.toUpperCase(),
      value: getChartValueByMetricsType(metrics, category),
    }
  })
}

const getChartValueByMetricsType = (metrics: TMetrics, category: TMetricsCategory) => {
  switch (category) {
    case NV_METRICS_CATEGORY:
      return 'notionalVolumeUsd' in metrics ? metrics.notionalVolumeUsd : 0
    case LOTS_METRICS_CATEGORY:
      return 'lots' in metrics ? metrics.lots : 0
    case FTD_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case DEPOSITS_METRICS_CATEGORY:
      return 'totalDeposits' in metrics ? metrics.totalDeposits : 0
    case NET_DEPOSITS_METRICS_CATEGORY:
      return 'totalNetDeposits' in metrics ? metrics.totalNetDeposits : 0
    case NEW_CLIENTS_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case ACTIVE_CLIENTS_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case OPENED_TA_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case ACTIVE_TA_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case IB_COMMISSIONS_METRICS_CATEGORY:
      return 'commission' in metrics ? metrics.commission : 0
    default:
      return 0
  }
}

export const getTotalValueByMetricsType = (
  data: TData | undefined,
  category: TMetricsCategory
): number => {
  if (!data) {
    return 0
  }

  return data.reduce((sum, metrics) => {
    return sum + getChartValueByMetricsType(metrics, category)
  }, 0)
}
